import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { generateTextHashes, type TextHashes } from '@/shared/hash'
import { useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const UTILITY = 'text-hash-generator'

type HashKey = keyof TextHashes

const HASH_ROWS: readonly { key: HashKey; label: string; legacy: boolean }[] = [
  { key: 'sha256', label: 'SHA-256', legacy: false },
  { key: 'sha1', label: 'SHA-1', legacy: true },
  { key: 'md5', label: 'MD5', legacy: true },
]

export function HashGeneratorPage() {
  const t = useT()
  const [input, setInput] = useState('')
  const [hashes, setHashes] = useState<TextHashes | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState<HashKey | null>(null)
  const requestIdRef = useRef(0)

  const handleGenerate = useCallback(async () => {
    if (!input) {
      setHashes(null)
      setPending(false)
      setError(false)
      return
    }

    const requestId = ++requestIdRef.current
    const start = performance.now()
    setPending(true)
    setError(false)
    toolInvoke(UTILITY, { action: 'generate', algorithms: 3, inputLength: input.length })

    try {
      const result = await generateTextHashes(input)
      if (requestId !== requestIdRef.current) return
      setHashes(result)
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        action: 'generate',
        algorithms: 3,
        inputLength: input.length,
      })
    } catch {
      if (requestId !== requestIdRef.current) return
      setHashes(null)
      setError(true)
      toolError(UTILITY, 'hash-failed', { action: 'generate', algorithms: 3 })
    } finally {
      if (requestId === requestIdRef.current) setPending(false)
    }
  }, [input])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void handleGenerate()
    }, 350)

    return () => {
      window.clearTimeout(timeout)
      requestIdRef.current += 1
    }
  }, [handleGenerate])

  const handleCopy = useCallback(async (key: HashKey) => {
    const value = hashes?.[key]
    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      setCopied(key)
      setTimeout(() => setCopied((current) => current === key ? null : current), 1500)
    } catch {
      setCopied(null)
    }
  }, [hashes])

  const handleClear = useCallback(() => {
    requestIdRef.current += 1
    setInput('')
    setHashes(null)
    setError(false)
    setCopied(null)
    setPending(false)
  }, [])

  return (
    <ToolPageShell toolKey="textHashGenerator" width="wide">
      <section className="space-y-4">
        <div className="rounded-xl border bg-card/90 p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={handleClear} className={!input && !hashes ? 'hidden' : undefined}>
              {t.hashGenerator.clear}
            </Button>
            {pending && (
              <span className="text-sm text-muted-foreground" role="status">
                {t.hashGenerator.generating}
              </span>
            )}
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t.hashGenerator.utf8Note}</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <Card className="flex min-h-0 flex-col overflow-hidden shadow-sm">
            <div className="flex min-h-14 items-center border-b bg-muted/45 px-4 py-3 text-sm font-medium">
              {t.hashGenerator.inputTitle}
            </div>
            <textarea
              value={input}
              onChange={(event) => {
                requestIdRef.current += 1
                setInput(event.target.value)
                setHashes(null)
                setError(false)
                setPending(false)
              }}
              placeholder={t.hashGenerator.inputPlaceholder}
              aria-label={t.hashGenerator.inputAriaLabel}
              className="min-h-[300px] w-full flex-1 resize-y border-0 bg-card p-5 font-mono text-[13px] leading-5 text-foreground placeholder:text-muted-foreground/55 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35 lg:min-h-[420px]"
              spellCheck={false}
            />
            <div className="border-t bg-muted/25 px-4 py-2 text-xs text-muted-foreground">
              {t.hashGenerator.characters}: {input.length}
            </div>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="flex min-h-14 items-center border-b bg-muted/45 px-4 py-3 text-sm font-medium">
              {t.hashGenerator.outputTitle}
            </div>
            <div className="space-y-4 p-4">
              {HASH_ROWS.map(({ key, label, legacy }) => (
                <div key={key} className="rounded-lg border bg-muted/20 p-4">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{label}</span>
                      {legacy && (
                        <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-900 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100">
                          {t.hashGenerator.legacy}
                        </span>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => void handleCopy(key)} className={!hashes ? 'hidden' : undefined}>
                      {copied === key ? t.hashGenerator.copied : t.hashGenerator.copy}
                    </Button>
                  </div>
                  <div className="min-h-10 break-all rounded bg-background px-3 py-2 font-mono text-xs leading-5 text-foreground">
                    {hashes?.[key] ?? t.hashGenerator.outputPlaceholder}
                  </div>
                </div>
              ))}
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/45 dark:text-amber-100">
                {t.hashGenerator.securityWarning}
              </p>
              {error && (
                <p className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                  {t.hashGenerator.error}
                </p>
              )}
            </div>
          </Card>
        </div>
      </section>
    </ToolPageShell>
  )
}
